import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Header from "../../components/Header";
import saasApi from "../../utils/saasApi";

const Users = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", username: "", role: "vigilante" });
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const notify = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await saasApi.getUsers());
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, notify]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setCreating(true);
    try {
      await saasApi.createUser({
        email: form.email,
        password: form.password,
        username: form.username || undefined,
        role: form.role,
      });
      setForm({ email: "", password: "", username: "", role: "vigilante" });
      notify("Usuario creado correctamente");
      fetchUsers();
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setCreating(false);
    }
  };

  const deleteUser = useCallback(async (id) => {
    const confirmed = window.confirm("Desactivar este usuario?");
    if (!confirmed) return;
    try {
      await saasApi.updateUserStatus(id, { is_active: false });
      notify("Usuario desactivado");
      fetchUsers();
    } catch (err) {
      notify(err.message, "error");
    }
  }, [fetchUsers, notify]);

  const columns = useMemo(
    () => [
      { field: "email", headerName: "Email", flex: 1.2, minWidth: 220 },
      { field: "username", headerName: "Usuario", flex: 0.8, minWidth: 160 },
      {
        field: "role",
        headerName: "Rol",
        width: 140,
        renderCell: ({ row }) => (
          <Chip
            icon={<SecurityOutlinedIcon />}
            label={row.role}
            color="primary"
            variant="outlined"
            size="small"
          />
        ),
      },
      {
        field: "is_active",
        headerName: "Estado",
        width: 130,
        renderCell: ({ row }) => (
          <Chip
            label={row.is_active === 0 ? "Inactivo" : "Activo"}
            color={row.is_active === 0 ? "default" : "success"}
            size="small"
          />
        ),
      },
      {
        field: "created_at",
        headerName: "Creado",
        width: 170,
        valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString("es-AR") : ""),
      },
      {
        field: "actions",
        headerName: "",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Tooltip title="Eliminar">
            <IconButton color="error" onClick={() => deleteUser(row.id)}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [deleteUser]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Header title="Usuarios" subtitle="Roles finales: owner, admin, vigilante, auditor y superadmin" />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <Paper
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: 3,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
            }}
          >
            <Stack spacing={2.2}>
              <Box display="flex" alignItems="center" gap={1}>
                <PersonAddAlt1Icon color="primary" />
              <Typography variant="h5">Nuevo usuario</Typography>
              </Box>

              <TextField
                label="Email"
                type="email"
                required
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                fullWidth
              />

              <TextField
                label="Nombre visible"
                value={form.username}
                onChange={(event) => setForm((prev) => ({ ...prev, username: event.target.value }))}
                fullWidth
              />

              <TextField
                select
                label="Rol"
                value={form.role}
                onChange={(event) => setForm((prev) => ({ ...prev, role: event.target.value }))}
                fullWidth
              >
                {["owner", "admin", "vigilante", "auditor", "superadmin"].map((role) => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </TextField>

              <TextField
                label="Contrasena"
                type={showPassword ? "text" : "password"}
                required
                value={form.password}
                onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowPassword((value) => !value)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={creating}
                startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <PersonAddAlt1Icon />}
              >
                Crear usuario
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Paper
            sx={{
              height: 520,
              p: 2,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5">Usuarios creados</Typography>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchUsers} disabled={loading}>
                Actualizar
              </Button>
            </Box>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            />
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snack.open}
        autoHideDuration={4200}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
